import { registerPlugin } from '@capacitor/core';
import { getNativeIosAuthHeaders, getNativeIosLinkedUser, setNativeIosLinkedUser, type NativeIosLinkedUser } from './nativeApiRouting';

interface TrialPlugin {
  getCredential(): Promise<{ credential: string }>;
  getDeviceToken(): Promise<{ deviceToken: string }>;
}
export interface DeviceTrialStatus {
  active: boolean; used: boolean; canTrial: boolean;
  expiresAt: string | null; serverNow: string;
  user?: NativeIosLinkedUser;
}
const plugin = registerPlugin<TrialPlugin>('ZiaDeviceTrial');
let leaseDeadline = 0;
export const DEVICE_TRIAL_LEASE_EVENT = 'zia:device-trial-lease';

export function deviceTrialRemainingMs() {
  return Math.max(0, leaseDeadline - performance.now());
}
export function clearDeviceTrialLease() {
  leaseDeadline = 0;
  window.dispatchEvent(new Event(DEVICE_TRIAL_LEASE_EVENT));
}
export async function getDeviceTrialStatus(start = false, signal?: AbortSignal): Promise<DeviceTrialStatus> {
  const { credential } = await plugin.getCredential();
  const device = start ? await plugin.getDeviceToken() : {};
  const requestStarted = performance.now();
  const response = await fetch('/api/auth/native-ios/trial', {
    method: 'POST', headers: { ...getNativeIosAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential, ...device, start }), signal: signal || AbortSignal.timeout(25000),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Unable to verify the free trial. Please retry.');
  const status = data as DeviceTrialStatus;
  // Trial access requires an online check at least once a minute. Use elapsed
  // monotonic time plus the SERVER deadline; the phone's clock is irrelevant.
  const remaining = status.expiresAt ? Date.parse(status.expiresAt) - Date.parse(status.serverNow) : 0;
  leaseDeadline = status.active ? requestStarted + Math.min(remaining, 60000) : 0;
  window.dispatchEvent(new Event(DEVICE_TRIAL_LEASE_EVENT));
  const linked = getNativeIosLinkedUser();
  if (status.active && status.user && (!linked || linked.userId.startsWith('zwn_trial_'))
    && (linked?.userId !== status.user.userId || linked?.sessionToken !== status.user.sessionToken)) {
    setNativeIosLinkedUser(status.user);
  }
  return status;
}
